import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { type ToolRefPreview, typePreviewOf } from '@crowdex/plugin-contracts';
import { cn } from '@crowdex/ui/lib/utils';
import { useEffect, useState } from 'react';
import { callApp } from './apps-client';
import { ArtifactView, WhenVisible } from './artifact-view';

/**
 * A compact preview drawn by the host (a ref's, or a type's preview route's): an image, the first
 * rows of a table, or a few lines of text, in `className`'s box. Nothing to draw: null.
 */
export function CompactPreview({
  preview,
  className,
}: {
  preview: ToolRefPreview | undefined;
  className: string;
}) {
  if (preview?.image !== undefined)
    return (
      <div className={cn('overflow-hidden bg-background', className)} data-compact-preview="image">
        <img alt="" className="size-full object-cover" src={preview.image} />
      </div>
    );
  if (preview?.rows !== undefined && preview.rows.length > 0)
    return (
      <div
        className={cn('overflow-hidden bg-background px-3 py-2', className)}
        data-compact-preview="rows"
      >
        <table className="type-meta w-full table-fixed border-collapse text-foreground/85">
          <tbody>
            {preview.rows.map((row, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: rows are only positions in a fixed preview.
              <tr className="border-hairline border-b last:border-b-0" key={index}>
                {row.map((cell, column) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: cells are only positions in a fixed preview.
                  <td className="truncate px-1.5 py-1" key={column}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  if (preview?.text !== undefined && preview.text.trim() !== '')
    return (
      <div
        className={cn('overflow-hidden bg-background px-3.5 py-2.5', className)}
        data-compact-preview="text"
      >
        <p className="line-clamp-6 whitespace-pre-line text-foreground/80 text-xs leading-5">
          {preview.text}
        </p>
      </div>
    );
  return null;
}

/**
 * An artifact in a card: its type's compact preview (`types[].preview`) when it has one (blank
 * until it answers), else its type's view in the `preview` container, as before.
 */
export function ArtifactPreview({
  artifact,
  className,
}: {
  artifact: ArtifactListingEntry;
  className: string;
}) {
  const preview = useArtifactPreview(artifact);
  if (preview === 'pending') return <div aria-hidden className={cn('bg-background', className)} />;
  if (preview !== undefined) return <CompactPreview className={className} preview={preview} />;
  return (
    <WhenVisible className={cn('pointer-events-none overflow-hidden bg-background', className)}>
      <ArtifactView artifact={artifact} container="preview" />
    </WhenVisible>
  );
}

/** The previews asked for, by artifact and version: each is asked once while it does not change. */
const asked = new Map<string, Promise<ToolRefPreview | null>>();
const MAX_ASKED = 200;

type Previewed = Pick<
  ArtifactListingEntry,
  'plugin' | 'space' | 'path' | 'conversation' | 'modifiedAt' | 'version' | 'preview'
>;

function previewKey(artifact: Previewed): string {
  return [
    artifact.plugin,
    artifact.space,
    artifact.path,
    artifact.version ?? artifact.modifiedAt,
  ].join('\u0000');
}

/**
 * An artifact's compact preview, from its type's preview route (`types[].preview`): `GET
 * <route>?space=…&path=…` of its app. Null when the type has none, the answer holds nothing usable
 * or the call fails: the card shows the type's view, as before.
 */
export function fetchArtifactPreview(artifact: Previewed): Promise<ToolRefPreview | null> {
  const route = artifact.preview;
  if (route == null) return Promise.resolve(null);
  const key = previewKey(artifact);
  const known = asked.get(key);
  if (known !== undefined) return known;
  const query = new URLSearchParams({ space: artifact.space, path: artifact.path });
  const pending = callApp({
    plugin: artifact.plugin,
    route: `${route}${route.includes('?') ? '&' : '?'}${query}`,
    method: 'GET',
    artifact: { space: artifact.space, path: artifact.path },
    ...(artifact.conversation === null ? {} : { conversation: artifact.conversation }),
  })
    .then(typePreviewOf)
    .catch(() => null);
  if (asked.size >= MAX_ASKED) asked.delete(asked.keys().next().value as string);
  asked.set(key, pending);
  return pending;
}

/**
 * `fetchArtifactPreview` for a card: `pending` until its route answers, then the preview, or
 * undefined when there is none (no artifact, no route, nothing usable), so the card draws what it
 * drew before.
 */
export function useArtifactPreview(
  artifact: Previewed | undefined,
): ToolRefPreview | 'pending' | undefined {
  const [preview, setPreview] = useState<{ key: string; value: ToolRefPreview | null } | null>(
    null,
  );
  const key = artifact?.preview == null ? null : previewKey(artifact);
  // The key names the artifact and its version: a new version is asked again, the same is not.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `artifact` is read through `key`.
  useEffect(() => {
    if (artifact === undefined || key === null) return;
    let live = true;
    void fetchArtifactPreview(artifact).then((value) => {
      if (live) setPreview({ key, value });
    });
    return () => {
      live = false;
    };
  }, [key]);
  if (key === null) return undefined;
  return preview !== null && preview.key === key ? (preview.value ?? undefined) : 'pending';
}
